"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.signOut = exports.refreshSession = exports.changePassword = exports.userHasSharedPermission = exports.userHasPermission = exports.notifyUserChanged = exports.userHasChanged = exports.clearCachedUser = exports.getAuthUser = exports.getAuthTokenData = exports.getAuthToken = exports.initialize = void 0;
const cognito_1 = __importDefault(require("./authentication/adapters/cognito"));
const cookies_1 = require("./authentication/cookies");
const errors_1 = require("./errors");
const events_1 = require("./events");
const config_1 = require("./config");
const queries_1 = require("./queries");
const logging_1 = require("./logging");
const remote_1 = require("./logs/remote");
const token_1 = require("./authentication/token");
const user_1 = require("./user");
// for now keeping it simple and only supporting cognito
const adapter = new cognito_1.default();
const getAuthenticationAdapter = () => {
    return adapter;
};
let currentConfig;
const goToSignOut = (returnToUrl) => {
    const cfg = currentConfig; // we guard against this being undef in outer funcs
    (0, cookies_1.setReturnToUrlCookie)({
        returnToUrl: returnToUrl === null || returnToUrl === void 0 ? void 0 : returnToUrl.trim(),
        env: cfg.env,
        apps: cfg.authentication.sharedAuthApps,
        domain: cfg.authentication.cookies.domain,
    });
    let signOutUrl = cfg.authentication.signOutUrl.trim();
    if (!signOutUrl) {
        signOutUrl = 'https://id.getbuilt.com/signout';
        (0, logging_1.error)('No sign out URL configured or provided. Using production sign out.');
        // gonna give a second so a dev can observe this behavior and give a chance or RUM console error tracking to grab it/report
        window.setInterval(() => {
            window.location.href = signOutUrl;
        }, 1000);
        return;
    }
    window.location.href = signOutUrl;
};
const tryAuthenticationOperation = async (operation, op) => {
    var _a, _b;
    if (!currentConfig) {
        return (0, errors_1.errorResult)('NOT_INITIALIZED', 'Auth utility not initialized. Call `initialize` first.');
    }
    let result = {
        ok: true,
    };
    try {
        (0, logging_1.verbose)(`${operation}...`);
        result = await op();
        if (result.ok) {
            (0, logging_1.verbose)(`${operation} OK:`, result);
        }
        else {
            (0, logging_1.warn)(`${operation} failed.`, result);
        }
    }
    catch (ex) {
        const message = `An unexpected error occurred while trying ${operation}.`;
        (0, logging_1.error)(message, ex);
        result = (0, errors_1.errorResult)('UNKNOWN', message, ex);
    }
    if (!result.ok) {
        if (((_a = result.error) === null || _a === void 0 ? void 0 : _a.code) !== 'NO_AUTH_SESSION') {
            // we want to avoid logging when there's no auth session because that is expected when
            // loading the app initially (e.g., via a bookmark or typing in URL) or due to timeout
            // we only want to log issues where a session may exist but is not valid or properly usable
            const adapter = getAuthenticationAdapter();
            const context = {
                operation,
                result: {
                    ok: result.ok,
                    data: result.data,
                    // we pass along the exception separately so it can be serialized with special handling
                    error: result.error ? { code: result.error.code, message: result.error.message } : undefined,
                },
                adapter: Object.assign({ name: adapter.name }, (typeof adapter.getLogData === 'function' ? adapter.getLogData() : {})),
            };
            (0, remote_1.shipError)(`${operation} failed.`, {
                context,
                exception: (_b = result.error) === null || _b === void 0 ? void 0 : _b.exception,
            });
        }
        if (currentConfig.authentication.goToSignOutOnFailure) {
            (0, logging_1.warn)(`Redirecting to sign out page due to ${operation} failure.`);
            if (!/skipredir=1/i.test(window.location.search)) {
                // we support skipping redir via querystring for debugging ease of use
                goToSignOut();
            }
        }
    }
    return result;
};
/**
 * Initialize front end auth utility/providers.
 *
 * @param config - auth configuration
 * @returns - if the operation was successful
 */
const initialize = async (config) => {
    const validationResult = (0, config_1.validateConfig)(config);
    if (!validationResult.ok) {
        return validationResult;
    }
    currentConfig = config;
    (0, remote_1.configure)(config);
    return await tryAuthenticationOperation('initialize', async () => {
        const initResult = await getAuthenticationAdapter().initialize(config);
        if (initResult.ok) {
            // start token monitor if configured
            // we do this in case (like Cognito) it won't immediately give us negative feedback
            // if the session is not valid when initializing
            const startTokenResult = await (0, token_1.startTokenMonitor)(config, getAuthenticationAdapter());
            if (!startTokenResult.ok) {
                return startTokenResult;
            }
        }
        return initResult;
    });
};
exports.initialize = initialize;
/**
 * Get the auth token for the current user.
 *
 * @returns - result including the auth token, if successful
 */
const getAuthToken = async () => {
    return await tryAuthenticationOperation('getAuthToken', async () => {
        var _a;
        // we use token monitor for consistency with getting token data
        const tokenDataResult = await (0, token_1.getMonitoredTokenData)();
        return {
            ok: tokenDataResult.ok,
            data: (_a = tokenDataResult.data) === null || _a === void 0 ? void 0 : _a.rawToken,
            error: tokenDataResult.error,
        };
    });
};
exports.getAuthToken = getAuthToken;
/**
 * Get the auth token data for the current user.
 *
 * @returns - result including the auth token data, if successful
 */
const getAuthTokenData = async () => {
    return await tryAuthenticationOperation('getAuthTokenData', () => (0, token_1.getMonitoredTokenData)());
};
exports.getAuthTokenData = getAuthTokenData;
/**
 * Get currently authenticated user information, which includes token data and profile from the configured `getUserQuery`.
 *
 * It is _recommended_ for MFEs to get the auth user from the `appContextUpdated` event in app-config-utility instead of calling this directly.
 * This advice applies for orchestrators that initialize and use that event to notify readiness, which is the new pattern adopted in CRE 2 timeframe.
 */
const getAuthUser = async (options = {}) => new Promise(async (resolve) => {
    var _a, _b, _c, _d, _e, _f;
    const caching = (_a = options.caching) !== null && _a !== void 0 ? _a : { enabled: false };
    let cachedUser;
    let authTokenData;
    if (caching.enabled) {
        cachedUser = await (0, user_1.getCachedUser)();
        if (cachedUser) {
            (0, logging_1.verbose)('Using cached user data.', cachedUser);
            // we'll use the cached token data as well, because getCachedUser only returns if the token matches
            authTokenData = cachedUser.token;
            // resolve so that calling code can continue, but we'll still try to get the latest user data below
            resolve({
                ok: true,
                data: cachedUser,
            });
        }
    }
    else {
        (0, user_1.clearCachedUser)();
    }
    if (!authTokenData) {
        // ^ may have it from cache
        const authTokenUserResult = await (0, exports.getAuthTokenData)();
        if (!authTokenUserResult.data) {
            return resolve(Object.assign(Object.assign({}, authTokenUserResult), { data: undefined }));
        }
        authTokenData = authTokenUserResult.data;
    }
    if (!(currentConfig === null || currentConfig === void 0 ? void 0 : currentConfig.authentication.getUserQuery)) {
        const message = 'No getUserQuery configured. It is required to get the authenticated user.';
        (0, logging_1.error)(message, currentConfig);
        return resolve((0, errors_1.errorResult)('BAD_CONFIG', message));
    }
    const userQueryDataResult = await (0, queries_1.executeQuery)(Object.assign(Object.assign({}, currentConfig.authentication.getUserQuery), { headers: Object.assign(Object.assign({}, currentConfig.authentication.getUserQuery.headers), { Authorization: `Bearer ${authTokenData.rawToken}` }) }));
    if (!userQueryDataResult.data) {
        (0, logging_1.error)('getUserQuery failed.', userQueryDataResult);
        return resolve(Object.assign(Object.assign({}, userQueryDataResult), { data: undefined }));
    }
    // we can't assume the received data is the expected shape, so we need to map it
    const userQueryData = userQueryDataResult.data;
    let profile;
    if (isUMUserData(userQueryData)) {
        // Map fields from the UM query response to the UserProfileData
        profile = {
            userUid: userQueryData.id,
            email: userQueryData.email,
            firstName: userQueryData.firstName,
            lastName: userQueryData.lastName,
            initials: `${userQueryData.firstName.charAt(0).toUpperCase()}${userQueryData.lastName
                .charAt(0)
                .toUpperCase()}`.trim(),
            fullName: `${userQueryData.firstName} ${userQueryData.lastName}`,
            preferredName: userQueryData.firstName,
            preferredLang: '',
            tenant: ((_b = userQueryData.currentTenant) === null || _b === void 0 ? void 0 : _b.id)
                ? adaptTenant(userQueryData.currentTenant)
                : adaptTenant(userQueryData.tenants.find((t) => t.id === userQueryData.tenantId)),
            tenants: userQueryData.tenants.map(adaptTenant),
            permissionGroups: (_c = userQueryData.permissionGroups) !== null && _c !== void 0 ? _c : [],
            sharedPermissionGroups: (_d = userQueryData.sharedPermissionGroups) !== null && _d !== void 0 ? _d : [],
        };
    }
    else if (isPMUserData(userQueryData)) {
        // Map fields from the PM query response to the UserProfileData
        const { firstName = '', lastName = '' } = userQueryData;
        profile = {
            userUid: userQueryData.userUid || '',
            email: userQueryData.email || authTokenData.email,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            initials: `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`.trim(),
            fullName: `${userQueryData.firstName} ${userQueryData.lastName}`,
            preferredName: firstName.trim(),
            preferredLang: '',
            tenant: adaptTenant(userQueryData.tenant),
            tenants: (_f = (_e = userQueryData.tenants) === null || _e === void 0 ? void 0 : _e.map(adaptTenant)) !== null && _f !== void 0 ? _f : [],
            permissionGroups: userQueryData.permissionGroups,
            sharedPermissionGroups: [],
        };
    }
    else {
        const message = "getUserQuery response data is not in expected shape. This likely indicates the orchestrator's authorization module is misconfigured.";
        (0, logging_1.error)(message, userQueryData);
        return resolve((0, errors_1.errorResult)('BAD_CONFIG', message));
    }
    // because the query is provided by orchestrator, we'll trust that due diligence was done to ensure the query is right
    // just logging verbose to help with diagnosing issues during that process
    (0, logging_1.verbose)('Mapped user/me query to UserProfileData. (rawData, mappedData)', userQueryData, profile);
    const authUser = Object.assign({ 
        // we copy some data from the token data to the profile for convenience
        lastLogin: authTokenData.lastLogin, token: authTokenData }, profile);
    if (caching.enabled) {
        (0, user_1.cacheUser)(authUser);
    }
    if ((0, user_1.notifyUserChanged)(authUser, cachedUser) && caching.userChanged) {
        caching.userChanged(authUser);
    }
    return resolve({
        ok: true,
        data: authUser,
    });
});
exports.getAuthUser = getAuthUser;
// export potentially useful functions from user funcs
var user_2 = require("./user");
Object.defineProperty(exports, "clearCachedUser", { enumerable: true, get: function () { return user_2.clearCachedUser; } });
Object.defineProperty(exports, "userHasChanged", { enumerable: true, get: function () { return user_2.userHasChanged; } });
Object.defineProperty(exports, "notifyUserChanged", { enumerable: true, get: function () { return user_2.notifyUserChanged; } });
var permission_1 = require("./authorization/permission");
Object.defineProperty(exports, "userHasPermission", { enumerable: true, get: function () { return permission_1.userHasPermission; } });
Object.defineProperty(exports, "userHasSharedPermission", { enumerable: true, get: function () { return permission_1.userHasSharedPermission; } });
/**
 * Change the currently signed in user's password.
 *
 * @param oldPassword - old/current password
 * @param newPassword - new password
 * @returns - if the operation was successful
 */
const changePassword = async (oldPassword, newPassword) => {
    return await tryAuthenticationOperation('changePassword', () => getAuthenticationAdapter().changePassword(oldPassword, newPassword));
};
exports.changePassword = changePassword;
/**
 * Refresh the currently authenticated user's auth session.
 *
 * If successful, the `authTokenUpdated` event will be fired and the new token will be returned.
 */
const refreshSession = async () => {
    const result = await tryAuthenticationOperation('changePassword', () => getAuthenticationAdapter().refreshSession());
    if (!result.ok) {
        return Object.assign(Object.assign({}, result), { data: undefined });
    }
    const authTokenUserResult = await (0, exports.getAuthTokenData)();
    if (!authTokenUserResult.data) {
        return authTokenUserResult;
    }
    events_1.authTokenUpdated.notify(authTokenUserResult.data);
    return authTokenUserResult;
};
exports.refreshSession = refreshSession;
/**
 * End current auth session and redirect to configured sign out.
 *
 * Use options to skip redirect or provide a custom return URL.
 *
 * @returns - if the operation was successful
 */
const signOut = async (options) => {
    var _a;
    const result = await tryAuthenticationOperation('changePassword', () => getAuthenticationAdapter().signOut());
    if (((_a = result.error) === null || _a === void 0 ? void 0 : _a.code) === 'NOT_INITIALIZED') {
        // if not initialized, we can't redirect to sign out cuz we don't have config
        return result;
    }
    if (!(options === null || options === void 0 ? void 0 : options.skipRedirect)) {
        goToSignOut(options === null || options === void 0 ? void 0 : options.returnToUrl);
    }
    return result; // still return result even if we're redirecting for consistency
};
exports.signOut = signOut;
const isUMUserData = (data) => {
    return 'id' in data && 'currentTenant' in data;
};
const isPMUserData = (data) => {
    return 'userUid' in data && 'email' in data && 'firstName' in data && 'lastName' in data;
};
const adaptTenant = (tenant) => {
    if (!tenant) {
        return {
            tenantUid: '',
            name: '',
        };
    }
    if ('tenantUid' in tenant) {
        // PM shape
        return {
            tenantUid: tenant.tenantUid,
            name: (tenant.name || '').trim(),
        };
    }
    else {
        // UM shape
        return {
            tenantUid: tenant.id,
            name: (tenant.name || '').trim(),
        };
    }
};
